import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ConfigurationFieldType } from '@ydistri/api-sdk';
import { ROOT_CATEGORY_ID } from '../_constants';

export type CategorySelectionType = 'exact' | 'parent' | false;

export interface EntityListSectionStatus {
  calculationId: number;
  categoryId: number;
  fieldType: ConfigurationFieldType;
  disabled: boolean;
}

export interface CategoryTreeState {
  currentlySelectedCategorySlug: string;
  selectedCategory: Record<string, CategorySelectionType | undefined>;
  openedCategories: Record<string, boolean | undefined>;
  entityListSectionStatus: EntityListSectionStatus[];
}

const rootCategorySlug = `${ROOT_CATEGORY_ID}`;
const initialState: CategoryTreeState = {
  currentlySelectedCategorySlug: rootCategorySlug,
  selectedCategory: { [rootCategorySlug]: 'exact' },
  openedCategories: { [rootCategorySlug]: true },
  entityListSectionStatus: [],
};

export const categoryTreeSlice = createSlice({
  name: 'categoryTreeReducer',
  initialState,
  reducers: {
    setSelectedCategory: (state, action: PayloadAction<string>) => {
      //setting all previous "selected" categories as not selected
      const previousCategories = state.currentlySelectedCategorySlug.split('-');
      let base = '';
      previousCategories.forEach(pt => {
        if (base !== '') base += '-';
        base += pt;
        state.selectedCategory[base] = false;
      });

      //setting all current categories as selected
      const currentCategories = action.payload.split('-');
      base = '';
      currentCategories.forEach(pt => {
        if (base !== '') base += '-';
        base += pt;
        state.selectedCategory[base] = 'parent';
      });

      //setting clicked category as 'exact' and as last
      state.selectedCategory[action.payload] = 'exact';
      state.currentlySelectedCategorySlug = action.payload;
    },
    setOpenedCategory: (
      state,
      action: PayloadAction<{ categorySlug: string; opened: boolean }>,
    ) => {
      state.openedCategories[action.payload.categorySlug] = action.payload.opened;
    },
    setEntityListSectionStatus: (state, action: PayloadAction<EntityListSectionStatus>) => {
      const newEntry = action.payload;
      const currentEntryIndex = state.entityListSectionStatus.findIndex(
        entry =>
          entry.calculationId === newEntry.calculationId &&
          entry.categoryId === newEntry.categoryId &&
          entry.fieldType === newEntry.fieldType,
      );
      if (currentEntryIndex !== -1) {
        if (!newEntry.disabled) {
          //we only care about disabled section, if the section is getting enabled, remove it from state to save space
          state.entityListSectionStatus.splice(currentEntryIndex, 1);
        } else {
          state.entityListSectionStatus[currentEntryIndex].disabled = newEntry.disabled;
        }
      } else {
        state.entityListSectionStatus.push(newEntry);
      }
    },
    resetState: () => initialState,
  },
});

export const { setSelectedCategory, setOpenedCategory, resetState, setEntityListSectionStatus } =
  categoryTreeSlice.actions;

export const categoryTreeReducer = categoryTreeSlice.reducer;
